import ReconnectingWebSocket from 'reconnecting-websocket';

import {
  clearDeviceToken,
  getAgentToken,
  getDeviceToken,
  setAgentToken,
  setDeviceToken,
} from '#/api/terminal/agent-url';

import { buildDeviceHandshake } from './device-identity';

import type {
  AgentConnectionStatus,
  GatewayFrame,
  GatewayRequest,
} from './types';

/**
 * Stable clientId prefix for the paradigm desktop block-trade terminal. The
 * full clientId is `${PARADIGM_BLOCK_TERMINAL_CLIENT_ID_PREFIX}:${shortUserId}`
 * — one per paradigm user, so each user has their own OpenClaw pairing.
 *
 * Pairings from the agent host must target this prefix. Example CLI:
 *   openclaw devices pair \
 *     --client-id paradigm-block-terminal:abcd1234 \
 *     --role operator --scopes operator.read,operator.write,operator.approvals
 */
export const PARADIGM_BLOCK_TERMINAL_CLIENT_ID_PREFIX =
  'paradigm-block-terminal';

export interface AgentWebSocket {
  readonly send: (content: string) => void;
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
  /**
   * Request a self-service pairing with the gateway. The current browser
   * clientId is included — the user approves it out-of-band (e.g. via
   * `openclaw devices approve --latest`), and the gateway emits
   * `node.pair.resolved` with a freshly minted token.
   */
  readonly requestPairing: (params?: {
    readonly scopes?: readonly string[];
  }) => Promise<string>;
  readonly onMessage: (handler: (frame: GatewayFrame) => void) => () => void;
  readonly onStatusChange: (
    handler: (status: AgentConnectionStatus, errorMessage?: string) => void,
  ) => () => void;
  readonly getStatus: () => AgentConnectionStatus;
  readonly close: () => void;
  readonly reconnect: () => void;
}

let nextReqId = 1;

function makeReqId(): string {
  return String(nextReqId++);
}

function buildClientId(userId: string): string {
  const lower = userId.toLowerCase();
  // userId may not be a hex string; use first 6 + last 4 if long enough,
  // otherwise just lowercased userId.
  const short =
    lower.length >= 10 ? `${lower.slice(0, 6)}${lower.slice(-4)}` : lower;
  return `${PARADIGM_BLOCK_TERMINAL_CLIENT_ID_PREFIX}:${short}`;
}

const DEFAULT_OPERATOR_SCOPES = [
  'operator.admin',
  'operator.read',
  'operator.write',
  'operator.approvals',
];

export function createAgentWebSocket(
  url: string,
  userId: string,
  /**
   * Pairing token to use if none is stored in localStorage for this user.
   * Supplied by the caller from the server-synced preferences blob so the
   * terminal can authenticate on a new device without re-entering the token.
   */
  fallbackPairingToken?: string,
): AgentWebSocket {
  let status: AgentConnectionStatus = 'connecting';
  let connectReqId: string | null = null;
  let sessionKey: string | null = null;
  let lastHandshakeUsedDeviceToken = false;
  let deviceTokenRetryAttempted = false;
  const clientId = buildClientId(userId);

  const messageHandlers = new Set<(frame: GatewayFrame) => void>();
  const statusHandlers = new Set<
    (status: AgentConnectionStatus, errorMessage?: string) => void
  >();
  const pendingRequests = new Map<
    string,
    { resolve: (v: unknown) => void; reject: (e: Error) => void }
  >();
  const pendingPairings = new Set<{
    resolve: (token: string) => void;
    reject: (e: Error) => void;
  }>();

  function setStatus(newStatus: AgentConnectionStatus, errorMsg?: string) {
    status = newStatus;
    for (const handler of statusHandlers) {
      handler(newStatus, errorMsg);
    }
  }

  function sendReq(method: string, params?: Record<string, unknown>): string {
    const id = makeReqId();
    const req: GatewayRequest = { type: 'req', id, method, params };
    ws.send(JSON.stringify(req));
    return id;
  }

  const ws = new ReconnectingWebSocket(url, [], {
    maxRetries: 3,
    connectionTimeout: 10_000,
    maxReconnectionDelay: 30_000,
  });

  ws.addEventListener('open', () => {
    setStatus('connected');
  });

  ws.addEventListener('close', () => {
    setStatus('disconnected');
    connectReqId = null;
  });

  ws.addEventListener('error', () => {
    setStatus('error');
  });

  async function handleChallenge(
    payload: { nonce?: string } | undefined,
  ): Promise<void> {
    setStatus('authenticating');
    const deviceToken = getDeviceToken(userId);
    const pairingToken = getAgentToken(userId) ?? fallbackPairingToken;
    const authToken = deviceToken ?? pairingToken ?? '';
    lastHandshakeUsedDeviceToken = deviceToken != null;

    const nonce =
      payload?.nonce ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

    // Gateway is configured with token-auth mode — only 'operator' is a valid
    // role. 'pairing' was intended for device-pairing flows but is rejected here.
    const role = 'operator';
    const scopes = DEFAULT_OPERATOR_SCOPES;

    let device;
    try {
      device = buildDeviceHandshake({
        clientId: 'webchat-ui',
        clientMode: 'webchat',
        role,
        scopes,
        token: authToken,
        nonce,
      });
    } catch {
      device = undefined;
    }

    connectReqId = sendReq('connect', {
      minProtocol: 3,
      maxProtocol: 3,
      role,
      scopes,
      client: {
        id: 'webchat-ui',
        version: '1.0.0',
        platform: 'browser',
        mode: 'webchat',
      },
      auth: { token: authToken },
      device,
    });
  }

  ws.addEventListener('message', (event: MessageEvent) => {
    const rawData =
      typeof event.data === 'string' ? event.data : String(event.data);

    let frame: GatewayFrame;
    try {
      frame = JSON.parse(rawData) as GatewayFrame;
    } catch {
      return;
    }

    if (frame.type === 'event') {
      const evt = frame;

      if (evt.event === 'connect.challenge') {
        handleChallenge(evt.payload as { nonce?: string } | undefined).catch(
          () => {
            // swallow: errors surface via setStatus('error') inside handler
          },
        );
        return;
      }

      if (evt.event === 'node.pair.resolved') {
        const newToken = (evt.payload as { token?: string } | undefined)?.token;
        if (newToken != null && newToken.length > 0) {
          setAgentToken(userId, newToken);
          for (const p of pendingPairings) p.resolve(newToken);
          pendingPairings.clear();
        }
        return;
      }
    }

    if (frame.type === 'res') {
      const res = frame;

      if (connectReqId != null && res.id === connectReqId) {
        connectReqId = null;
        if (res.ok) {
          const payload = res.payload as
            | {
                snapshot?: {
                  sessionDefaults?: { mainSessionKey?: string };
                };
                auth?: { deviceToken?: string };
              }
            | undefined;
          sessionKey =
            payload?.snapshot?.sessionDefaults?.mainSessionKey ?? null;
          const newDeviceToken = payload?.auth?.deviceToken;
          if (newDeviceToken != null && newDeviceToken.length > 0) {
            setDeviceToken(userId, newDeviceToken);
          }
          // Seed localStorage with the fallback token so future reconnects
          // and the needsPairing check in the UI can find it without the
          // caller having to pass the fallback again.
          if (
            fallbackPairingToken != null &&
            fallbackPairingToken.length > 0 &&
            getAgentToken(userId) == null
          ) {
            setAgentToken(userId, fallbackPairingToken);
          }
          deviceTokenRetryAttempted = false;
          setStatus('authenticated');
        } else if (lastHandshakeUsedDeviceToken && !deviceTokenRetryAttempted) {
          // Gateway rejected our stored device token (likely rotated
          // server-side). Clear it and reconnect with the pairing token —
          // the gateway will issue a fresh device token in `hello-ok`.
          deviceTokenRetryAttempted = true;
          clearDeviceToken(userId);
          setStatus('connecting');
          ws.reconnect();
        } else {
          const errMsg = res.error?.message ?? 'Authentication failed';
          setStatus('error', errMsg);
          ws.close();
          for (const handler of messageHandlers) {
            handler(frame);
          }
        }
        return;
      }

      const pending = pendingRequests.get(res.id);
      if (pending != null) {
        pendingRequests.delete(res.id);
        if (res.ok) {
          pending.resolve(res.payload);
        } else {
          pending.reject(new Error(res.error?.message ?? 'Request failed'));
        }
        return;
      }
    }

    for (const handler of messageHandlers) {
      handler(frame);
    }
  });

  return {
    send(content: string) {
      sendReq('chat.send', {
        sessionKey: sessionKey ?? undefined,
        idempotencyKey: `msg-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        message: content,
      });
    },

    async request<T = unknown>(
      method: string,
      params?: Record<string, unknown>,
    ): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        if (ws.readyState !== WebSocket.OPEN) {
          reject(new Error('Gateway not connected'));
          return;
        }
        const id = makeReqId();
        pendingRequests.set(id, {
          resolve: resolve as (v: unknown) => void,
          reject,
        });
        const req: GatewayRequest = { type: 'req', id, method, params };
        ws.send(JSON.stringify(req));
      });
    },

    async requestPairing({
      scopes = DEFAULT_OPERATOR_SCOPES,
    }: { readonly scopes?: readonly string[] } = {}) {
      if (ws.readyState !== WebSocket.OPEN) {
        throw new Error('Gateway not connected');
      }
      return new Promise<string>((resolve, reject) => {
        pendingPairings.add({ resolve, reject });
        const id = makeReqId();
        pendingRequests.set(id, {
          resolve: () => {
            // res ok just means the request was accepted; the token arrives
            // via the node.pair.resolved event, which resolves pendingPairings.
          },
          reject: (e) => {
            for (const p of pendingPairings) p.reject(e);
            pendingPairings.clear();
          },
        });
        const req: GatewayRequest = {
          type: 'req',
          id,
          method: 'node.pair.request',
          params: {
            clientId,
            role: 'operator',
            scopes,
          },
        };
        ws.send(JSON.stringify(req));
      });
    },

    onMessage(handler) {
      messageHandlers.add(handler);
      return () => {
        messageHandlers.delete(handler);
      };
    },

    onStatusChange(handler) {
      statusHandlers.add(handler);
      return () => {
        statusHandlers.delete(handler);
      };
    },

    getStatus() {
      return status;
    },

    close() {
      ws.close();
      messageHandlers.clear();
      statusHandlers.clear();
      for (const [, p] of pendingRequests) {
        p.reject(new Error('Connection closed'));
      }
      pendingRequests.clear();
      for (const p of pendingPairings) {
        p.reject(new Error('Connection closed'));
      }
      pendingPairings.clear();
    },

    reconnect() {
      ws.reconnect();
    },
  };
}
